// Optional one-time recommendation. All purchases share the theme's guarded picker.
import { resolveInitialRecommendation, failVariantUpdate } from "velorart-variant-safety";

const initializedForms = new WeakSet();
const touchedForms = new WeakSet();
const initializedQuickBuys = new WeakMap();

function formForPicker(picker) {
  return picker && document.forms.namedItem(picker.getAttribute("form-id"));
}

function rememberInteraction(event) {
  if (!(event.target instanceof Element)) return;
  const form = formForPicker(event.target.closest("variant-picker"));
  if (form) touchedForms.add(form);
}

for (const eventName of ["pointerdown", "keydown", "input", "change"]) {
  document.addEventListener(eventName, rememberInteraction, { capture: true });
}

document.addEventListener("variant:add", (event) => {
  const drawer = event.target instanceof Element && event.target.closest("quick-buy-drawer");
  if (drawer) initializedQuickBuys.delete(drawer);
}, { capture: true });

class VelorartSizeBadgeDefault extends HTMLElement {
  async connectedCallback() {
    await customElements.whenDefined("variant-picker");
    if (!this.isConnected) return;
    const picker = this.closest("variant-picker");
    const form = formForPicker(picker);
    if (!form) return;
    try {
      const drawer = picker.hasAttribute("data-va-quick-buy") && picker.closest("quick-buy-drawer");
      if (!drawer && !picker.hasAttribute("update-url")) return;
      const quickBuyKey = `${picker.productHandle}-${picker.getAttribute("section-id")}`;
      if (drawer) {
        if (initializedQuickBuys.get(drawer) === quickBuyKey) return;
        initializedQuickBuys.set(drawer, quickBuyKey);
      } else {
        if (initializedForms.has(form)) return;
        initializedForms.add(form);
        const params = new URL(window.location.href).searchParams;
        if (["variant", "option_values", "selling_plan"].some((key) => params.has(key))) return;
      }
      if (touchedForms.has(form) || picker.contains(document.activeElement)) return;
      const controls = Array.from(form.elements).filter((input) => input.matches("input[data-option-position]"));
      if (controls.some((input) => input.checked !== input.defaultChecked)) return;
      const position = this.getAttribute("option-position");
      const valueId = this.getAttribute("option-value-id");
      const candidate = controls.find((input) => input.getAttribute("data-option-position") === position && input.value === valueId);
      if (!candidate || candidate.disabled || candidate.hasAttribute("data-product-url")) return;
      if (String(picker.selectedVariant?.id) === this.getAttribute("variant-id")) return;
      const active = controls.filter((input) => input.checked).sort((a, b) => Number(a.getAttribute("data-option-position")) - Number(b.getAttribute("data-option-position")));
      if (!active.some((input) => input.getAttribute("data-option-position") === position)) return;
      const optionValues = active.map((input) => input.getAttribute("data-option-position") === position ? valueId : input.value);
      await picker.selectCombination({ optionValues, productChange: false, initial: true });
    } catch (error) {
      failVariantUpdate(form, null);
      console.warn("The recommended selection could not be initialized.", error);
    } finally {
      // Also releases the initial HTML lock when an explicit URL/user choice wins.
      resolveInitialRecommendation(form);
    }
  }
}

// Each size is an unbroken text span. Reduce only an overflowing line's type;
// observe its container so drawer, popover, font and viewport changes are covered.
class VelorartSizeLine extends HTMLElement {
  connectedCallback() {
    this.scheduleFit = () => {
      cancelAnimationFrame(this.fitFrame);
      this.fitFrame = requestAnimationFrame(() => this.fit());
    };
    this.resizeObserver = new ResizeObserver(this.scheduleFit);
    this.resizeObserver.observe(this.parentElement);
    document.fonts?.addEventListener("loadingdone", this.scheduleFit);
    document.fonts?.ready.then(() => { if (this.isConnected) this.scheduleFit(); });
    this.scheduleFit();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.fitFrame);
    document.fonts?.removeEventListener("loadingdone", this.scheduleFit);
  }

  fit() {
    if (!this.isConnected || !this.clientWidth) return;
    this.style.setProperty("--va-size-scale", "1");
    if (this.scrollWidth <= this.clientWidth + 1) return;
    let low = 0.05, high = 1;
    for (let step = 0; step < 10; step++) {
      const scale = (low + high) / 2;
      this.style.setProperty("--va-size-scale", String(scale));
      if (this.scrollWidth <= this.clientWidth) low = scale;
      else high = scale;
    }
    this.style.setProperty("--va-size-scale", String(low));
  }
}

if (!customElements.get("velorart-size-line")) {
  customElements.define("velorart-size-line", VelorartSizeLine);
}
if (!customElements.get("velorart-size-badge-default")) {
  customElements.define("velorart-size-badge-default", VelorartSizeBadgeDefault);
}
